import React from 'react';
import { Plus, Trash2, X, ChevronDown, Edit, Users, UserCheck } from 'lucide-react';
import { teamService } from '../../verve-teams/services/teamService';
import { ROLE_LABELS } from '../../verve-teams/utils/teamMemberLabels';
import { Button } from '@/components/ui/Button';
import { assignTeamMembers, getLeadExecutionById } from '../services/leadExecutionService';

export const LeadExecutionTeamAssignmentForm = ({ project, onUpdate, onClose, initialEditMember = null }) => {
  const [selectedMembers, setSelectedMembers] = React.useState([]);
  const [allEmployees, setAllEmployees] = React.useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = React.useState(true);
  const [selectedRole, setSelectedRole] = React.useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [assignError, setAssignError] = React.useState('');
  
  React.useEffect(() => {
    if (project?.fieldOfficers) {
       setSelectedMembers(project.fieldOfficers);
    }
  }, [project]);

  React.useEffect(() => {
    const fetchTeamMembers = async () => {
      setIsLoadingEmployees(true);
      try {
        const res = await teamService.getEmployees({ limit: 1000 });
        const list = Array.isArray(res) ? res : (res?.data || res?.employees || res?.items || []);
        setAllEmployees(list);
      } catch (err) {
        console.error("Error loading team members:", err);
        setAssignError("Failed to fetch team members from backend.");
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    fetchTeamMembers();
  }, []);

  React.useEffect(() => {
    if (initialEditMember) {
       const roleVal = initialEditMember.installationTrade || initialEditMember.role || '';
       setSelectedRole(roleVal);
       const empId = initialEditMember._id || initialEditMember.id || initialEditMember;
       if (empId) setSelectedEmployeeId(empId);
    }
  }, [initialEditMember]);

  const roleOptions = React.useMemo(() => {
    const tradeMap = new Map();
    
    allEmployees.forEach(emp => {
      const r = emp.installationTrade || (emp.role && emp.role !== 'installation_team' ? emp.role : null) || emp.role;
      if (r) {
        const key = r.toLowerCase();
        /* Commented out per user request: "comment the carpenter also" */
        if (key === 'carpenter') return;
        if (!tradeMap.has(key)) {
          const formatted = ROLE_LABELS[key] || r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' ');
          tradeMap.set(key, formatted);
        }
      }
    });
    return Array.from(tradeMap.entries()).map(([value, label]) => ({ value, label }));
  }, [allEmployees]);

  const availableEmployees = React.useMemo(() => {
    if (!selectedRole) return [];
    const target = selectedRole.toLowerCase();
    return allEmployees.filter(emp => {
      const empTrade = (emp.installationTrade || '').toLowerCase();
      const empRole = (emp.role || '').toLowerCase();
      return empTrade === target || empRole === target || (empRole === 'installation_team' && empTrade === target);
    });
  }, [allEmployees, selectedRole]);

  const handleRemove = async (id) => {
     const nextMembers = selectedMembers.filter(m => (m._id || m.id || m) !== id);
     setSelectedMembers(nextMembers);
     try {
       const newIds = nextMembers.map(m => m._id || m.id || m);
       await assignTeamMembers(project._id, newIds);
       const updatedProject = await getLeadExecutionById(project._id);
       if (onUpdate) onUpdate(updatedProject);
     } catch (err) {
       console.error(err);
     }
  };

  const handleAddOrUpdate = async () => {
    if (!selectedEmployeeId || !selectedRole) return;
    setIsSubmitting(true);
    setAssignError('');
    try {
      const empToAdd = allEmployees.find(e => (e._id || e.id) === selectedEmployeeId);
      if (!empToAdd) {
        setAssignError("Selected team member not found.");
        setIsSubmitting(false);
        return;
      }
      const empId = empToAdd._id || empToAdd.id;
      if (selectedMembers.some(m => (m._id || m.id || m) === empId)) {
        setAssignError("This team member is already assigned to the project.");
        setIsSubmitting(false);
        return;
      }

      const nextMembers = [...selectedMembers, empToAdd];
      setSelectedMembers(nextMembers);
      setSelectedEmployeeId('');
      
      const newIds = nextMembers.map(m => m._id || m.id || m);
      await assignTeamMembers(project._id, newIds);
      const updatedProject = await getLeadExecutionById(project._id);
      if (onUpdate) onUpdate(updatedProject);
    } catch (err) {
      console.error(err);
      const msg = err?.data?.message || err?.message || 'Action failed';
      setAssignError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAll = async () => {
    setAssignError('');
    setIsSubmitting(true);
    try {
      const newIds = selectedMembers.map(m => m._id || m.id || m);
      await assignTeamMembers(project._id, newIds);
      const updatedProject = await getLeadExecutionById(project._id);
      if (onUpdate) onUpdate(updatedProject);
      onClose();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Could not assign team members';
      setAssignError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 font-poppins">
      {assignError && <p className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{assignError}</p>}
      
      {/* Member Selection Area */}
      <div className="flex flex-col sm:flex-row items-end gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
         <div className="w-full sm:w-[240px]">
            <label className="text-[12px] font-semibold text-slate-600 mb-1.5 block">Select Role</label>
            <div className="relative">
               <select
                  value={selectedRole}
                  onChange={(e) => {
                     setSelectedRole(e.target.value);
                     setSelectedEmployeeId('');
                  }}
                  className="w-full h-[40px] px-3 text-[13px] border border-slate-300 rounded-lg text-slate-700 bg-white focus:outline-none focus:ring-1 focus:border-[#f97316] focus:ring-[#f97316]/20 appearance-none cursor-pointer"
               >
                  <option value="">-- Select Role --</option>
                  {roleOptions.map(t => (
                     <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
               </select>
               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-400" />
               </div>
            </div>
         </div>

         <div className="flex-1 w-full min-w-0">
            <label className="text-[12px] font-semibold text-slate-600 mb-1.5 block">Select Member Name</label>
            <div className="relative">
               <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  disabled={!selectedRole || isLoadingEmployees || availableEmployees.length === 0}
                  className="w-full h-[40px] px-3 text-[13px] border border-slate-300 rounded-lg text-slate-700 bg-white focus:outline-none focus:ring-1 focus:border-[#f97316] focus:ring-[#f97316]/20 appearance-none cursor-pointer disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
               >
                  <option value="">
                     {isLoadingEmployees
                        ? "Loading team members..."
                        : !selectedRole
                        ? "-- Select a role first --"
                        : availableEmployees.length === 0
                        ? "No members found with this role"
                        : "-- Select Member --"}
                  </option>
                  {availableEmployees.map(emp => (
                     <option key={emp._id || emp.id} value={emp._id || emp.id}>
                        {emp.name} {emp.phone ? `(${emp.phone})` : ''} - {(emp.installationTrade || emp.role || 'Member').toUpperCase()}
                     </option>
                  ))}
               </select>
               <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-400" />
               </div>
            </div>
         </div>

         <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
               variant="orange"
               onClick={handleAddOrUpdate}
               disabled={!selectedEmployeeId || !selectedRole || isSubmitting}
               loading={isSubmitting}
               className="h-[40px] whitespace-nowrap rounded-lg px-6 font-bold flex-1 sm:flex-initial"
            >
               <Plus className="w-4 h-4 mr-2" />
               Add
            </Button>
         </div>
      </div>

      {/* Selected Members Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden mt-2 bg-white shadow-sm">
         <table className="w-full text-left border-collapse">
            <thead>
               <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-5 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest w-1/2">Name</th>
                  <th className="px-5 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest w-1/3">Role Name</th>
                  <th className="px-5 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest w-1/6 text-right">Action</th>
               </tr>
            </thead>
            <tbody>
               {selectedMembers.length === 0 ? (
                  <tr>
                     <td colSpan="3" className="px-5 py-10 text-center text-slate-400 text-[13px] italic bg-white">
                        No team members assigned yet.
                     </td>
                  </tr>
               ) : (
                  selectedMembers.map((m, idx) => (
                     <tr key={m._id || m.id || idx} className="bg-white border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5">
                           <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 text-slate-600 flex items-center justify-center font-bold text-[12px]">
                                 {(m.name?.[0] || 'M').toUpperCase()}
                              </div>
                              <div className="flex flex-col">
                                 <span className="text-[13px] font-bold text-slate-800">{m.name}</span>
                                 {m.phone && <span className="text-[11px] text-slate-400 font-medium">{m.phone}</span>}
                              </div>
                           </div>
                        </td>
                        <td className="px-5 py-3.5">
                           <span className="text-[11px] uppercase font-black tracking-widest text-[#2b448f] bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-md">
                              {m.installationTrade || m.role || 'Member'}
                           </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                           <div className="flex items-center justify-end gap-1">
                              <button
                                 type="button"
                                 onClick={() => handleRemove(m._id || m.id || m)}
                                 className="text-slate-400 hover:text-rose-500 p-2 rounded-lg hover:bg-rose-50 transition-colors"
                                 title="Remove Member"
                              >
                                 <Trash2 className="w-4 h-4" />
                              </button>
                           </div>
                        </td>
                     </tr>
                  ))
               )}
            </tbody>
         </table>
      </div>

      {/* Footer Actions */}
      <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-5 mt-2">
        <Button
           variant="secondary"
           onClick={onClose}
           className="w-full sm:flex-1 h-11 rounded-lg text-[13px] font-bold"
        >
           Cancel
        </Button>
        <Button
           variant="orange"
           onClick={handleSaveAll}
           disabled={isSubmitting}
           loading={isSubmitting}
           className="w-full sm:flex-1 h-11 rounded-lg text-[13px] font-bold"
        >
           {isSubmitting ? '' : 'Save Assignments'}
        </Button>
      </div>
    </div>
  );
};
